import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '../../lib/cn'
import { useBloqueoScroll } from '../../lib/bloqueoScroll'
import { BotonIcono } from './base'

/** Hoja modal: sube desde abajo en teléfono, aparece centrada en
 *  pantallas grandes. Es el único contenedor modal de la app — un solo
 *  patrón que aprender. */
export function Hoja({
  abierta,
  onCerrar,
  titulo,
  descripcion,
  children,
  pie,
  ancho = 'md',
  textoCerrar,
}: {
  abierta: boolean
  onCerrar: () => void
  titulo: string
  descripcion?: string
  children: ReactNode
  pie?: ReactNode
  ancho?: 'sm' | 'md' | 'lg'
  /** Por defecto el botón de arriba es solo la "X". Cuando esta hoja
   *  representa un paso de una tarea más larga (como elegir productos
   *  dentro de una venta) conviene un texto explícito —"OK", "Listo"—
   *  en vez del ícono, para que se sienta como confirmar un paso y no
   *  como cancelar algo. */
  textoCerrar?: string
}) {
  useBloqueoScroll(abierta)

  useEffect(() => {
    if (!abierta) return
    const cerrarConEsc = (e: KeyboardEvent) => e.key === 'Escape' && onCerrar()
    document.addEventListener('keydown', cerrarConEsc)
    return () => document.removeEventListener('keydown', cerrarConEsc)
  }, [abierta, onCerrar])

  if (!abierta) return null

  const anchos = { sm: 'sm:max-w-md', md: 'sm:max-w-xl', lg: 'sm:max-w-3xl' }[ancho]

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6">
      <div
        className="absolute inset-0 bg-piedra-950/40 backdrop-blur-sm anim-fundido"
        onClick={onCerrar}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        className={cn(
          'relative flex max-h-[92dvh] w-full flex-col superficie-elevada',
          'rounded-t-3xl sm:rounded-3xl border-t sm:border borde-suave sombra-flotante',
          'anim-hoja sm:anim-aparecer',
          anchos,
        )}
      >
        {/* Asa de arrastre: señal táctil de que la hoja se puede cerrar. */}
        <div className="flex justify-center pt-2.5 sm:hidden">
          <div className="h-1 w-9 rounded-full bg-piedra-300 dark:bg-piedra-700" />
        </div>

        <header className="flex items-start gap-3 px-5 pt-4 pb-3 sm:px-6 sm:pt-6">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-xl font-bold texto-base">{titulo}</h2>
            {descripcion && <p className="mt-0.5 text-sm texto-suave">{descripcion}</p>}
          </div>
          {textoCerrar ? (
            <button
              onClick={onCerrar}
              className="-mt-1 -mr-1 h-9 shrink-0 rounded-full bg-bosque-500 px-4 text-[13px] font-bold text-white transition-colors hover:bg-bosque-600"
            >
              {textoCerrar}
            </button>
          ) : (
            <BotonIcono etiqueta="Cerrar" onClick={onCerrar} className="-mt-1 -mr-1">
              <X className="size-5" />
            </BotonIcono>
          )}
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 sm:px-6">{children}</div>

        {pie && (
          <footer className="flex gap-3 border-t borde-suave px-5 py-4 safe-bottom sm:px-6">{pie}</footer>
        )}
      </div>
    </div>,
    document.body,
  )
}

/** Confirmación destructiva. Nombra la acción, no pregunta "¿estás
 *  seguro?": el botón dice exactamente qué va a pasar. */
export function Confirmar({
  abierta,
  titulo,
  detalle,
  textoConfirmar,
  onConfirmar,
  onCerrar,
}: {
  abierta: boolean
  titulo: string
  detalle: string
  textoConfirmar: string
  onConfirmar: () => void
  onCerrar: () => void
}) {
  return (
    <Hoja abierta={abierta} onCerrar={onCerrar} titulo={titulo} ancho="sm">
      <p className="text-[15px] leading-relaxed texto-suave">{detalle}</p>
      <div className="mt-6 flex gap-3">
        <button
          onClick={onCerrar}
          className="h-12 flex-1 rounded-2xl border borde-base font-semibold texto-base transition-colors hover:superficie-hundida"
        >
          Cancelar
        </button>
        <button
          onClick={() => {
            onConfirmar()
            onCerrar()
          }}
          className="h-12 flex-1 rounded-2xl bg-rojo-500 font-semibold text-white transition-colors hover:bg-rojo-600"
        >
          {textoConfirmar}
        </button>
      </div>
    </Hoja>
  )
}
